import { BankAccountDetails } from '@/types/bankAccounts';
import { SVLink, SVSummaryCard } from '../core';
import { SVSummaryCardItem } from '@/types/common';
import { formatDateTimeFromUTC } from '@/utils/date';
import { formatCurrency } from '@/utils/currency';
import BankAccountStatusLabel from '../BankAccountStatusLabel';
import { ROUTES } from '@/constants/routes';
import { AuthorizedTeammate } from '@/types/authorizedTeammates';
import { Stack } from '@mui/material';
import TeammateLink from '../TeammateLink';

type BankAccountDetailsBasicInfoProps = {
  accountDetails: BankAccountDetails;
  authorizedTeammates: AuthorizedTeammate[];
};

export default function BankAccountDetailsBasicInfo({
  accountDetails,
  authorizedTeammates,
}: BankAccountDetailsBasicInfoProps) {
  const {
    uuid,
    status,
    accountNumberLast4,
    routingNumber,
    accountTypeStr,
    savingsInterestRate,
    savingsInterestRateLastUpdatedAt,
    apy,
    availableBalance,
    currentBalance,
    openedAt,
    owners,
    advisor,
  } = accountDetails;

  const items: SVSummaryCardItem[] = [
    {
      label: 'ID',
      text: uuid,
    },
    { label: 'Account Number', text: `******${accountNumberLast4}` },
    { label: 'Routing Number', text: routingNumber },
    {
      label: 'Account Owners',
      customContent: (
        <Stack spacing={1}>
          {owners.map(owner => (
            <SVLink
              key={`owner${owner.id}`}
              to={`${ROUTES.CUSTOMERS}/${owner.id}`}>
              {owner.profile.fullName}
            </SVLink>
          ))}
        </Stack>
      ),
    },
    { label: 'Account Type', text: accountTypeStr },
    {
      label: 'Savings Interest Rate',
      text: `${savingsInterestRate}% ${savingsInterestRateLastUpdatedAt ? `(${formatDateTimeFromUTC(savingsInterestRateLastUpdatedAt, true)})` : ''}`,
    },
    { label: 'APY', text: `${apy}%` },
    {
      label: 'Available Balance',
      text: formatCurrency({ amount: availableBalance.value }),
    },
    {
      label: 'Current Balance',
      text: formatCurrency({ amount: currentBalance.value }),
    },
    {
      label: 'Status',
      customContent: <BankAccountStatusLabel status={status} />,
    },
    {
      label: 'Account Opening Date',
      text: formatDateTimeFromUTC(openedAt, true),
    },
    {
      label: 'Advisor',
      customContent: (
        <TeammateLink
          uuid={advisor.uuid}
          name={`${advisor.firstName} ${advisor.lastName}`}
        />
      ),
    },
    {
      label: 'Authorized Teammates',
      customContent: authorizedTeammates.length ? (
        <Stack spacing={1}>
          {authorizedTeammates.map(teammate => (
            <TeammateLink
              key={`authorizedTeammate${teammate.uuid}`}
              uuid={teammate.uuid}
              name={`${formatDateTimeFromUTC(teammate.authorizedAt)} - ${teammate.teammateFirstName} ${teammate.teammateLastName}`}
            />
          ))}
        </Stack>
      ) : null,
    },
  ];
  return <SVSummaryCard label="Basic Info" items={items} />;
}
